/* Created by Interactive 63 - https://interactive63.com */
/**
 * Connects morph tracks in a project to the morph builder: expands repeating tracks, finds the
 * fill rule, and collects the shapes, warnings and static poses the compiler needs.
 */
import type { CompileWarning } from '../compiler/types';
import type { ElementId, Project, Track } from '../model/types';
import { isMorphValue } from '../model/types';
import { effectiveKeyframes } from '../timeline/repeat';
import { buildMorphTrack, decimalsFor, type MorphBuildResult, type MorphFrameInput } from './build';
import { formatPath } from './format';

export const COMPLEX_SEGMENTS = 600;

export interface MorphBuild {
  track: Track;
  elementId: ElementId;
  result: MorphBuildResult;
  /** The path data of the first shape after normalisation, for the export's initial `d`. */
  initial: string | null;
  /** The path data of the last shape after normalisation. */
  final: string | null;
  /** A single-shape track, which is a constant rather than an animation. */
  constant: string | null;
}

let cachedSource = '';
let cachedRules = new Map<string, boolean>();

/** Whether an element uses the evenodd fill rule, from its attribute or inline style. */
function usesEvenOdd(project: Project, elementId: ElementId): boolean {
  if (project.svg.source !== cachedSource) {
    cachedSource = project.svg.source;
    cachedRules = new Map();
    const doc = new DOMParser().parseFromString(cachedSource, 'image/svg+xml');
    for (const el of Array.from(doc.querySelectorAll('[data-st-id]'))) {
      const id = el.getAttribute('data-st-id');
      if (!id) continue;
      const style = /fill-rule\s*:\s*([a-z]+)/.exec(el.getAttribute('style') ?? '')?.[1];
      cachedRules.set(id, (style ?? el.getAttribute('fill-rule')) === 'evenodd');
    }
  }
  return cachedRules.get(elementId) ?? false;
}

/** Collects the morph tracks that will be exported or previewed. */
export function buildMorphs(
  project: Project,
  isIncluded: (elementId: ElementId) => boolean,
): MorphBuild[] {
  const builds: MorphBuild[] = [];
  const decimals = decimalsFor(project.svg.viewBox);
  for (const track of project.tracks) {
    if (track.property !== 'morph' || !track.enabled || track.keyframes.length === 0) continue;
    if (!isIncluded(track.elementId)) continue;
    const keyframes = effectiveKeyframes(track, project.scene.duration).keyframes;
    const frames: MorphFrameInput[] = [];
    for (const kf of keyframes) {
      if (isMorphValue(kf.value)) {
        frames.push({
          time: kf.time,
          d: kf.value.d,
          easing: kf.easing,
          align: kf.value.align,
          ...(kf.value.pins ? { pins: kf.value.pins } : {}),
          ...(kf.value.parts ? { parts: kf.value.parts } : {}),
        });
      }
    }
    if (frames.length === 0) continue;
    if (frames.length === 1) {
      builds.push({
        track,
        elementId: track.elementId,
        result: { ok: false, message: 'A single shape does not animate.' },
        initial: null,
        final: null,
        constant: frames[0]?.d ?? null,
      });
      continue;
    }
    const result = buildMorphTrack({
      elementId: track.elementId,
      frames,
      evenOdd: usesEvenOdd(project, track.elementId),
      decimals,
      once: track.restartWithScene === false,
    });
    const data = result.ok ? result.data : null;
    builds.push({
      track,
      elementId: track.elementId,
      result,
      initial: data ? formatPath(data.template, data.frames[0]?.values ?? []) : null,
      final: data
        ? formatPath(data.template, data.frames[data.frames.length - 1]?.values ?? [])
        : null,
      constant: null,
    });
  }
  return builds;
}

/** Warnings about morph tracks: unreadable shapes and shapes that may be slow. */
export function morphWarnings(builds: readonly MorphBuild[], project: Project): CompileWarning[] {
  const warnings: CompileWarning[] = [];
  for (const build of builds) {
    const label = project.elements[build.elementId]?.label ?? build.elementId;
    if (!build.result.ok && build.constant === null) {
      warnings.push({
        code: 'morph-invalid',
        message: `The shape morph on ${label} couldn't be built: ${build.result.message}`,
        elementId: build.elementId,
      });
    } else if (build.result.ok && build.result.problems.length > 0) {
      warnings.push({
        code: 'morph-pins',
        message: `Some pins on the morph of ${label} were ignored: ${build.result.problems[0] ?? ''}`,
        elementId: build.elementId,
      });
    } else if (build.result.ok && build.result.segments > COMPLEX_SEGMENTS) {
      warnings.push({
        code: 'morph-complex',
        message: `The morph on ${label} is complex and may be slow on low-powered devices.`,
        elementId: build.elementId,
      });
    }
  }
  return warnings;
}
